import Pagination from '../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { FiMoreVertical } from 'react-icons/fi';
import Modal from '../../components/shared/modal';
import EditModal from './edit-modal';
import ConfirmationModal from '../../components/shared/confirmation-modal';
import { statusColors } from '../../utils/status-color-utils';
import { RiArrowUpSLine } from 'react-icons/ri';
import { RxCaretSort } from 'react-icons/rx';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { deleteRenewal, updateRenewalStatus } from '../../services/renewal-service';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import { formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import Loader from '../../components/shared/loader';
import AddModal from './add-modal';
import AuditLogModal from '../../components/shared/AuditLogModal';

const RenewalTable = ({ data, setData, setSelectedRows, selectedRows, vendors, vehicles, globalDataloading, fetchGlobalData, types, accountId, loadingData, fetchRenewals, onClearNewlyAddedItem, setNewlyAddedItem, newlyAddedItem, globalFilter, onGlobalFilterChange }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sorting, setSorting] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isAuditLogModalOpen, setIsAuditLogModalOpen] = useState(false);

  // Internal state for row selection if not provided by parent
  const [internalSelectedRows, setInternalSelectedRows] = useState({});
  const finalSelectedRows = selectedRows ?? internalSelectedRows;
  const finalSetSelectedRows = setSelectedRows ?? setInternalSelectedRows;

  const groupedData = useMemo(() => {
    const grouped = data.reduce((acc, row) => {
      const lineageId = row.lineageId;
      if (!acc[lineageId]) {
        acc[lineageId] = [];
      }
      acc[lineageId].push(row);
      return acc;
    }, {});

    return Object.values(grouped).map(group => {
      const sortedGroup = [...group].sort((a, b) =>
        new Date(b.createdOn) - new Date(a.createdOn)
      );
      const parentRow = sortedGroup[0];
      return {
        ...parentRow,
        subRows: sortedGroup.slice(1),
      };
    });
  }, [data]);

  useEffect(() => {
    if (newlyAddedItem) {
      setSelectedRow(newlyAddedItem);
      setIsEditModalOpen(true);
      setNewlyAddedItem(null);
    }
  }, [newlyAddedItem, setNewlyAddedItem]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleActionClick = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
    if (typeof fetchGlobalData === 'function') {
      fetchGlobalData();
    }
  };

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  const handleRowClick = (row, e) => {
    if (
      !e.target.closest('input[type="checkbox"]') &&
      !e.target.closest('.expander-button')
    ) {
      setSelectedRow(row.original);
      setIsEditModalOpen(true);
      if (typeof fetchGlobalData === 'function') {
        fetchGlobalData();
      }
    }
  };

  const handleConfirmAction = (action, row) => {
    setSelectedRow(row.original);
    if (action === 'Renew') {
      setIsRenewalModalOpen(true);
    } else if (action === 'Cancel') {
      setConfirmAction(action);
      setIsConfirmModalOpen(true);
    } else {
      setConfirmAction(action);
      setIsConfirmModalOpen(true);
    }
    setErrorMessage(null);
    closeMenu();
  };

  const handleConfirm = async () => {
    if (!selectedRow || !confirmAction) return;

    if (confirmAction === 'delete') {
      try {
        setIsDeleting(true);
        const renewalId = selectedRow.renewalId;
        const res = await deleteRenewal(accountId, renewalId);
        if (res.status === 204) {
          setData(prevData => prevData.filter(item => item.renewalId !== renewalId));
          setIsConfirmModalOpen(false);
          setSelectedRow(null);
          setConfirmAction(null);
          setSuccessMessage('Renewal deleted successfully!');
        } else {
          setErrorMessage(res.data?.message || 'Failed to delete Renewal entry');
        }
      } catch (error) {
        console.error('Error deleting Renewal:', error);
        setErrorMessage('An error occurred while deleting the Renewal entry');
      } finally {
        setIsDeleting(false);
      }
    } else {
      try {
        setIsCanceling(true);
        const renewalId = selectedRow.renewalId;
        const res = await updateRenewalStatus(accountId, renewalId, 'cancelled');
        if (res.success) {
          setData(prev =>
            prev.map(item =>
              item.renewalId === renewalId
                ? { ...item, status: 'cancelled' }
                : item
            )
          );
          setIsConfirmModalOpen(false);
          setSelectedRow(null);
          setConfirmAction(null);
          setSuccessMessage('Renewal Cancelled successfully!');
        } else {
          setErrorMessage(res.data?.message || 'Failed to Cancelled Renewal');
        }
      } catch (error) {
        console.error('Error cancelling Renewal:', error);
        setErrorMessage('An error occurred while cancelling the Renewal');
      } finally {
        setIsCanceling(false);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'expander',
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              {...{
                onClick: row.getToggleExpandedHandler(),
                style: { cursor: 'pointer' },
              }}
              className="p-1 expander-button"
            >
              {row.getIsExpanded() ? (
                <FiChevronDown className="text-gray-600" />
              ) : (
                <FiChevronRight className="text-gray-600" />
              )}
            </button>
          ) : (
            <div className="" />
          );
        },
        size: 20,
        maxSize: 40,
      },
      {
        id: 'select',
        header: ({ table }) => (
          <div className="flex justify-center w-full">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.getToggleAllRowsSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        cell: ({ row }) => {
          if (row.depth > 0) return null;
          return (
            <div className="flex justify-center w-full" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={row.getIsSelected()}
                onChange={(e) => {
                  e.stopPropagation();
                  row.getToggleSelectedHandler()(e);
                }}
                className="w-4 h-4"
              />
            </div>
          );
        },
        enableSorting: false,
        size: 20,
        maxSize: 40,
      },
      {
        accessorKey: 'renewalId',
        header: <div className="">Renewal Id</div>,
        enableSorting: true,
        size: 100,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue()}</span>,
      },
      {
        accessorKey: 'lineageId',
        header: 'Lineage Id',
        enableSorting: true,
        size: 100,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue()}</span>,
      },
      {
        accessorKey: 'vehicle.name',
        header: 'Vehicle',
        enableSorting: true,
        size: 120,
        maxSize: 200,
        cell: (info) => <span className="truncate">{info.getValue()}</span>,
      },
      {
        accessorKey: 'vendor.name',
        header: 'Vendor',
        enableSorting: true,
        size: 120,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue() || ''}</span>,
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: true,
        size: 100,
        maxSize: 120,
        cell: (info) => <span className="truncate">{info.getValue() ? camelCaseToWords(String(info.getValue())) : '----'}</span>,
      },
      {
        accessorKey: 'startTime',
        header: 'Start Time',
        enableSorting: true,
        size: 150,
        maxSize: 180,
        cell: (info) => <span className="truncate">{formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a')}</span>,
      },
      {
        accessorKey: 'endTime',
        header: 'End Time',
        enableSorting: true,
        size: 150,
        maxSize: 180,
        cell: (info) => <span className="truncate">{formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a')}</span>,
      },
      {
        accessorKey: 'remind',
        header: 'Remind (Days)',
        enableSorting: true,
        size: 130,
        maxSize: 150,
        cell: (info) => (
          <div className="pl-5">
            <span className="truncate">{info.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        size: 120,
        maxSize: 140,
        cell: (info) => {
          const status = String(info.getValue() || '').toLowerCase() || 'unknown';
          const color = statusColors[status] || statusColors.default;
          return (
            <div className="flex justify-start">
              <span
                className="py-0.5 rounded-md text-sm font-medium capitalize text-center truncate"
                style={{
                  backgroundColor: color,
                  color: 'black',
                  border: `1px solid ${color}`,
                  width: '100px',
                  display: 'inline-block',
                  textAlign: 'center',
                }}
              >
                {status}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => {
          const rowId = row.id;
          return (
            <div className=" flex justify-center w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(rowId);
                }}
                className="p-1 rounded-full hover:bg-gray-200"
              >
                <FiMoreVertical className="text-gray-600 text-sm" />
              </button>
              {openMenuId === rowId && row.original.status != 'renewed' && (
                <div
                  className="absolute right-0 w-32 bg-white rounded-md shadow-md z-10 border border-gray-200 top-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('Renew', row)}
                  >
                    Renew
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('Cancel', row)}
                  >
                    Cancel
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setSelectedRow(row.original);
                      setIsAuditLogModalOpen(true);
                      closeMenu();
                    }}
                  >
                    Audit Log
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-red-600 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('delete', row)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 50,
        // maxSize: 50,
      },
    ],
    [openMenuId]
  );

  const table = useReactTable({
    data: groupedData,
    columns,
    state: {
      sorting,
      globalFilter,
      expanded,
      rowSelection: finalSelectedRows,
    },
    onExpandedChange: setExpanded,
    getSubRows: row => row.subRows || [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableSortingRemoval: false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onRowSelectionChange: finalSetSelectedRows,
    globalFilterFn: (row, columnId, filterValue) => {
      const searchLower = filterValue.toLowerCase();
      let rowValue = '';

      if (columnId.includes('.')) {
        const [parent, child] = columnId.split('.');
        rowValue = String(row.original[parent]?.[child] || '').toLowerCase();
      } else if (columnId === 'startTime' || columnId === 'endTime') {
        rowValue = formatUTCTimestampToLocal(row.getValue(columnId), 'yyyy-MM-dd hh:mm a').toLowerCase();
      } else {
        rowValue = String(row.getValue(columnId)).toLowerCase();
      }

      return rowValue.includes(searchLower);
    },
  });

  return (
    <div className="w-full">
      {successMessage && (
        <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
          {errorMessage}
        </div>
      )}

      <div className="overflow-x-auto max-w-full pb-[105px]">
        {loadingData ? (
          <Loader />
        ) : (
          <table className="min-w-full text-sm border border-gray-200" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '20px', maxWidth: '40px' }} />
              <col style={{ width: '20px', maxWidth: '40px' }} />
              <col style={{ width: '80px', maxWidth: '100px' }} />
              <col style={{ width: '80px', maxWidth: '100px' }} />
              <col style={{ width: '120px', maxWidth: '200px' }} />
              <col style={{ width: '120px', maxWidth: '150px' }} />
              <col style={{ width: '100px', maxWidth: '120px' }} />
              <col style={{ width: '150px', maxWidth: '180px' }} />
              <col style={{ width: '150px', maxWidth: '180px' }} />
              <col style={{ width: '80px', maxWidth: '100px' }} />
              <col style={{ width: '120px', maxWidth: '140px' }} />
              <col style={{ width: '50px', maxWidth: '50px' }} />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-2 py-1.5 border-b capitalize truncate"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        width: header.column.getSize(),
                        maxWidth: header.column.columnDef.maxSize,
                      }}
                    >
                      <div className="flex items-center gap-1 truncate">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-black">
                            {header.column.getIsSorted() === 'asc' ? (
                              <RiArrowUpSLine className="text-base mt-0.5" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <RiArrowUpSLine className="text-base rotate-180" />
                            ) : (
                              <RxCaretSort className="text-lg" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table
                .getRowModel()
                .rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((row) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`hover:bg-gray-50 relative cursor-pointer ${row.depth > 0 ? 'bg-gray-50' : ''}`}
                      onClick={(e) => handleRowClick(row, e)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`px-2 py-1 border-b truncate ${row.depth > 0 ? 'pl-2' : ''}`}
                          style={{
                            width: cell.column.getSize(),
                            maxWidth: cell.column.columnDef.maxSize,
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isRenewalModalOpen && selectedRow}
        onClose={() => setIsRenewalModalOpen(false)}
        modalClass="xl:w-[40%] lg:w-[45%] md:w-[55%] sm:w-[70%] w-[90%]"
      >
        <AddModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          context="renew"
          vehicles={vehicles}
          fetchRenewals={fetchRenewals}
          vendors={vendors}
          types={types}
          handleCloseAddModal={() => setIsRenewalModalOpen(false)}
          globalDataloading={globalDataloading}
          fetchGlobalData={fetchGlobalData}
        />
      </Modal>

      <Modal
        isOpen={isEditModalOpen && selectedRow}
        onClose={() => setIsEditModalOpen(false)}
        modalClass="xl:w-[40%] lg:w-[45%] md:w-[55%] sm:w-[70%] w-[90%]"
      >
        <EditModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setIsEditModalOpen={setIsEditModalOpen}
          setData={setData}
          vendors={vendors}
          vehicles={vehicles}
          types={types}
          globalDataloading={globalDataloading}
        />
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen && selectedRow}
        onClose={() => setIsConfirmModalOpen(false)}
        modalClass="xl:!w-[30%] lg:!w-[35%] md:!w-[40%] sm:w-[60%] w-[90%]"
      >
        <ConfirmationModal
          confirmAction={confirmAction}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          handleConfirm={handleConfirm}
          label="Renewal"
          loading={isCanceling || isDeleting}
        />
      </Modal>

      <Modal
        isOpen={isAuditLogModalOpen && selectedRow}
        onClose={() => {
          setIsAuditLogModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:w-[60%] lg:w-[70%] md:w-[80%] sm:w-[90%] w-[95%]"
      >
        <AuditLogModal
          isOpen={isAuditLogModalOpen}
          onClose={() => {
            setIsAuditLogModalOpen(false);
            setSelectedRow(null);
          }}
          accountId={accountId}
          linkedType="renewal"
          linkedId={selectedRow?.renewalId}
        />
      </Modal>

      <Pagination
        currentPage={currentPage}
        totalItems={table.getFilteredRowModel().rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default RenewalTable;